// Reusable confirmation dialog

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from '@chakra-ui/react';
import { useRef } from 'react';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  colorScheme?: string;
}

/**
 * Consistent confirmation dialog for destructive actions
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  colorScheme = 'red',
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={isLoading ? () => {} : onClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent bg="bg-surface">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>{message}</AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              {cancelText}
            </Button>
            <Button
              colorScheme={colorScheme}
              // Every caller manages its own onClose — synchronously at the end of a
              // sync handler, or from a mutation's onSuccess/onSettled/finally once the
              // async work actually resolves. Closing here too, based on isLoading's
              // value at click time (still stale/false — state hasn't re-rendered yet),
              // closed the dialog before an async onConfirm ever got to show its
              // spinner: the dialog just vanished with no visible in-progress state.
              onClick={onConfirm}
              ml={3}
              isLoading={isLoading}
              loadingText={confirmText}
            >
              {confirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
